import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, PhoneIcon } from 'lucide-react';
// Enhanced FAQ content with more conversational, question-based format for voice search
const faqs = [{
  question: 'How often should I service my AC system?',
  answer: 'We recommend servicing your AC system at least twice a year: once before the cooling season begins and once before the heating season starts. Regular maintenance helps prevent breakdowns, extends the life of your system, and ensures optimal energy efficiency.'
}, {
  question: 'What size AC unit do I need for my home?',
  answer: 'The size of the AC unit needed depends on several factors including the square footage of your home, ceiling height, insulation quality, window efficiency, and local climate. Our technicians will perform a detailed load calculation to determine the perfect size for your specific needs, ensuring optimal comfort and efficiency.'
}, {
  question: 'How long does an AC installation typically take?',
  answer: 'A standard residential AC installation typically takes 1-2 days to complete. However, more complex installations or those requiring ductwork modifications may take longer. Our team will provide you with a specific timeframe after assessing your particular situation.'
}, {
  question: 'What are the signs that my AC needs repair?',
  answer: "Common signs include: unusual noises, reduced airflow, warm air coming from vents, frequent cycling on and off, high humidity in your home, water leaks around the unit, and unexplained increases in your energy bill. If you notice any of these signs, it's best to call for service before a small issue becomes a major problem."
}, {
  question: 'Do you offer emergency AC repair services?',
  answer: 'Yes, we provide 24/7 emergency AC repair services throughout Southwest Florida. Our technicians are always on call to help restore your comfort as quickly as possible, even on weekends and holidays, with no after-hours upcharges.'
}, {
  question: 'How can I improve the air quality in my home?',
  answer: 'There are several ways to improve indoor air quality: regular replacement of air filters, professional duct cleaning, installation of air purifiers or UV light systems, proper ventilation, and regular maintenance of your HVAC system. Our technicians can assess your specific needs and recommend the best solutions for your home.'
}, {
  question: 'How much does a new AC system cost in Southwest Florida?',
  answer: 'The cost of a new AC system in Southwest Florida typically ranges from $5,000 to $10,000, depending on the size of your home, the efficiency rating of the system, and any additional features. We offer financing options to help make this investment more affordable.'
}, {
  question: 'Why is my electric bill so high in the summer?',
  answer: 'High summer electric bills are usually caused by your AC system working harder to maintain comfortable temperatures during extreme heat. Other factors include poor insulation, leaky ductwork, an aging AC system, or incorrect thermostat settings. Our energy assessment can identify the specific causes in your home.'
}];
export const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggleFaq = index => {
    setOpenIndex(openIndex === index ? null : index);
  };
  // Create FAQ schema for structured data
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
  return <section id="faq" className="py-20 relative" data-id="element-146">
      {/* Add structured data for FAQs */}
      <script type="application/ld+json" data-id="element-147">{JSON.stringify(faqSchema)}</script>
      {/* Background image with overlay and blur */}
      <div className="absolute inset-0 z-0" data-id="element-148">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pMsAjvDQ3F1uZefZfTBLhd/commercial-building-hvac-ceiling-installation-2025-03-15-02-41-01-utc.jpg" alt="HVAC background" className="w-full h-full object-cover filter blur-[2px]" data-id="element-149" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-gray-900/90" data-id="element-150"></div>
      </div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-151">
        <div className="text-center mb-12" data-id="element-152">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-600/20 text-blue-200 font-semibold text-sm mb-4 backdrop-blur-sm" data-id="element-153">
            FAQ
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-5" data-id="element-154">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed" data-id="element-155">
            Find answers to common questions about our HVAC services.
          </p>
        </div>
        <div className="max-w-3xl mx-auto" data-id="element-156">
          {faqs.map((faq, index) => <div key={index} className="mb-4 border border-blue-300/20 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-blue-900/20 backdrop-blur-sm bg-white/10" data-id="element-157">
              <button className="flex justify-between items-center w-full p-5 text-left hover:bg-white/5 transition-colors duration-300" onClick={() => toggleFaq(index)} aria-expanded={openIndex === index} data-id="element-158">
                <h3 className="text-lg font-semibold text-white" data-id="element-159">
                  {faq.question}
                </h3>
                <span className="ml-4 flex-shrink-0" data-id="element-160">
                  {openIndex === index ? <ChevronUpIcon className="h-5 w-5 text-blue-300" data-id="element-161" /> : <ChevronDownIcon className="h-5 w-5 text-blue-200" data-id="element-162" />}
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-163">
                <div className="p-5 pt-0 border-t border-blue-300/20" data-id="element-164">
                  <p className="text-blue-100" data-id="element-165">{faq.answer}</p>
                </div>
              </div>
            </div>)}
        </div>
        <div className="text-center mt-12" data-id="element-166">
          <p className="text-blue-100 mb-6" data-id="element-167">
            Still have questions? We're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-168">
            <a href="#request-service" className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-md font-medium transition-all duration-300 shadow-md hover:shadow-lg" data-id="element-169">
              Contact Us
            </a>
            <a href="tel:2398886268" className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm border-2 border-blue-300/30 text-blue-100 hover:bg-white/20 px-6 py-3.5 rounded-md font-medium transition-all duration-300 shadow-sm hover:shadow-md" data-id="element-170">
              <PhoneIcon className="mr-2 h-4 w-4" data-id="element-171" />
              Call: 239-888-6268
            </a>
          </div>
        </div>
      </div>
    </section>;
};